import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Building2, Contact, Loader2, Mail, MessageCircle, Plus, Trash2, UserMinus } from 'lucide-react';
import { useApi } from '@/hooks/useApi';
import { api } from '@/lib/api';
import { formatDateTime } from '@/lib/utils';
import { ORIGEN_BAJA, type Audiencia } from '@/lib/comunicaciones';
import LoadingSpinner from '@/components/ui/LoadingSpinner';

const TODOS: Audiencia = {
  suscriptores: { incluir: true, origenes: [], idiomas: [] },
  propietarios: { incluir: true, estados: ['active'], incluirSecundario: false },
};

/**
 * A quién se puede escribir y quién ha dicho que no. Los suscriptores se
 * gestionan en Leads → Suscritos (los guarda el portal); aquí está la lista de
 * bajas del CRM, que vale para todas las campañas.
 */
export default function ContactosPage() {
  const recuento = useApi(() => Promise.all([
    api.recuentoAudiencia({ ...TODOS, propietarios: { ...TODOS.propietarios, incluir: false } }),
    api.recuentoAudiencia({ ...TODOS, suscriptores: { ...TODOS.suscriptores, incluir: false } }),
    api.recuentoAudiencia({ ...TODOS, propietarios: { ...TODOS.propietarios, incluir: false } }, 'whatsapp'),
  ]), []);
  const bajas = useApi(() => api.getBajasComunicaciones(), []);
  const [email, setEmail] = useState('');
  const [motivo, setMotivo] = useState('');
  const [trabajando, setTrabajando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const anadir = async () => {
    if (!email.trim()) return;
    setTrabajando(true);
    setError(null);
    try {
      await api.anadirBajaComunicaciones(email.trim(), motivo.trim() || undefined);
      setEmail('');
      setMotivo('');
      bajas.refresh();
      recuento.refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo');
    } finally {
      setTrabajando(false);
    }
  };
  const quitar = async (e: string) => {
    if (!window.confirm(`¿Volver a escribir a ${e}?`)) return;
    try {
      await api.quitarBajaComunicaciones(e);
      bajas.refresh();
      recuento.refresh();
    } catch (err) {
      window.alert(err instanceof Error ? err.message : 'No se pudo');
    }
  };

  const [sus, props, wa] = recuento.data || [];

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      <div className="flex items-center gap-2">
        <Contact size={24} className="text-sereno-dark" />
        <div>
          <h1 className="text-xl sm:text-2xl font-bold">Contactos</h1>
          <p className="text-sm text-sereno-dark/40 mt-0.5">A quién pueden ir las campañas y quién se ha dado de baja.</p>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Cifra icono={Mail} etiqueta="Suscriptores confirmados" valor={sus?.total} pie={<Link to="/leads/suscritos" className="underline">Ver en Leads → Suscritos</Link>} cargando={recuento.loading} />
        <Cifra icono={MessageCircle} etiqueta="Con permiso de WhatsApp" valor={wa?.total} pie="Suscriptores confirmados con teléfono" cargando={recuento.loading} />
        <Cifra icono={Building2} etiqueta="Propietarios activos con email" valor={props?.total} pie="Solo comunicados de servicio" cargando={recuento.loading} />
        <Cifra icono={UserMinus} etiqueta="Bajas" valor={bajas.data?.length} pie="No reciben ninguna campaña" cargando={bajas.loading} />
      </div>

      <div className="card p-0 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100">
          <div className="font-semibold text-sereno-dark text-sm">Lista de bajas</div>
          <p className="text-xs text-sereno-dark/40 mt-0.5">
            Quien se da de baja desde un correo entra aquí solo, igual que las direcciones que rebotan. Las bajas de suscriptores llegan también al portal.
            Solo se pueden quitar las puestas a mano y los rebotes: quien se dio de baja él mismo vuelve suscribiéndose otra vez.
          </p>
        </div>
        <div className="px-4 py-3 border-b border-gray-100 flex flex-wrap gap-2">
          <input className="input flex-1 min-w-[200px]" type="email" placeholder="email@ejemplo.com" value={email} onChange={e => setEmail(e.target.value)} />
          <input className="input flex-1 min-w-[200px]" placeholder="Motivo (opcional): lo pidió por teléfono…" value={motivo} onChange={e => setMotivo(e.target.value)} />
          <button type="button" onClick={anadir} disabled={trabajando || !email.trim()} className="btn-primary text-sm flex items-center gap-1.5">
            {trabajando ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Dar de baja
          </button>
          {error && <div className="w-full text-sm text-red-600">{error}</div>}
        </div>
        {bajas.loading && !bajas.data ? <LoadingSpinner /> : (
          <table className="w-full text-sm">
            <tbody className="divide-y divide-gray-100">
              {(bajas.data || []).map(b => (
                <tr key={b.id}>
                  <td className="px-4 py-2">
                    <div className="text-sereno-dark flex items-center gap-1.5">
                      {b.telefono ? <><MessageCircle size={13} className="text-emerald-600" /> {b.telefono}{b.email && <span className="text-sereno-dark/40 text-xs">· {b.email}</span>}</> : b.email}
                    </div>
                    {b.detalle && <div className="text-xs text-sereno-dark/40">{b.detalle}</div>}
                  </td>
                  <td className="px-4 py-2 text-xs text-sereno-dark/60 hidden sm:table-cell">{ORIGEN_BAJA[b.origen]}{b.tipo ? ` · ${b.tipo}` : ''}</td>
                  <td className="px-4 py-2 text-xs text-sereno-dark/40 whitespace-nowrap">{formatDateTime(b.en)}</td>
                  <td className="px-2 py-2 text-right">
                    {(b.origen === 'manual' || b.origen === 'rebote') && (
                      <button type="button" onClick={() => quitar(b.id)} className="p-1.5 text-sereno-dark/30 hover:text-red-600" title="Quitar de la lista">
                        <Trash2 size={14} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {bajas.data?.length === 0 && (
                <tr><td className="px-4 py-6 text-center text-sm text-sereno-dark/40">Nadie se ha dado de baja todavía.</td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function Cifra({ icono: Icono, etiqueta, valor, pie, cargando }: {
  icono: React.ElementType; etiqueta: string; valor?: number; pie: React.ReactNode; cargando: boolean;
}) {
  return (
    <div className="card p-3 sm:p-4 min-w-0">
      <div className="flex items-start gap-1.5 text-xs text-sereno-dark/40"><Icono size={14} className="shrink-0 mt-px" /> {etiqueta}</div>
      <div className="text-3xl font-bold text-sereno-dark tabular-nums mt-1">
        {valor ?? (cargando ? <Loader2 size={20} className="animate-spin text-sereno-dark/30" /> : '—')}
      </div>
      <div className="text-xs text-sereno-dark/40 mt-0.5">{pie}</div>
    </div>
  );
}
